<script lang="ts">import { getContext } from 'svelte';
import emblaCarouselSvelte from 'embla-carousel-svelte';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
import { isAtLeastSmallScreen } from '../../../helpers/browser';
const { options = {}, plugins = [], slides = [], slidesToShow = 1, slidesToShowOnMobile = 1, 
// The gutter of the grid and the gap between each slide is the same value on mobile, so the user does not see any
// of the next slide
columnsOnMobile = 11, 
// Variable used to hold references to each slide so they can be referenced when using CarouselStage
slideElements = [], slide: slideFromProps, onCarouselInit, onSlideChange, class: className = '', ...rest } = $props();
const { emblaApi, canScrollPrev, canScrollNext } = getContext('carousel');
const NUMBER_OF_GRID_COLUMNS = 12;
// Variable used to prevent some functions (like onSlideChange) from running on initialization
let initialized = false;
// Variable used to track the left most index, so the slideChange event is only dispatched if the index changes
let previousIndex = null;
let align = 'center';
// Variable used to track if the carousel can scroll to the previous or next slide
let isScrollable = $state(false);
// Variable used to change the cursor from auto to grab when the carousel is being dragged
// This is used instead of using the Class Name plugin (https://www.embla-carousel.com/plugins/class-names/)
let isDragging = $state(false);
function updateAriaHidden(api) {
    // https://www.embla-carousel.com/api/methods/#slidenodes
    const allSlides = api.slideNodes();
    // https://www.embla-carousel.com/api/methods/#selectedscrollsnap
    const currentIndex = api.selectedScrollSnap();
    allSlides.forEach((slide, idx) => {
        const isVisible = idx >= currentIndex && idx < currentIndex + slidesToShow;
        slide.setAttribute('aria-hidden', `${!isVisible}`);
        if (isVisible) {
            slide.removeAttribute('inert');
        }
        else {
            slide.setAttribute('inert', '');
        }
    });
}
function getAlignValue() {
    // If the slide is supposed to take up the full width, then it should always align at the start no matter what the
    // screen width is
    if (columnsOnMobile === NUMBER_OF_GRID_COLUMNS) {
        return 'start';
    }
    return isAtLeastSmallScreen() ? 'start' : 'center';
}
function handleCarouselUpdate(api) {
    // https://www.embla-carousel.com/guides/previous-and-next-buttons/
    canScrollPrev.set(api.canScrollPrev());
    canScrollNext.set(api.canScrollNext());
    updateAriaHidden(api);
    isScrollable = api.canScrollPrev() || api.canScrollNext();
    if (initialized) {
        // https://www.embla-carousel.com/api/methods/#selectedscrollsnap
        const currentIndex = api.selectedScrollSnap();
        if (currentIndex !== previousIndex) {
            previousIndex = currentIndex;
            onSlideChange?.({ currentIndex });
        }
    }
}
// https://www.embla-carousel.com/api/events/#init
function onInit(event) {
    const api = event.detail;
    align = getAlignValue();
    if (emblaApi) {
        emblaApi.set(api);
    }
    onCarouselInit?.({ api });
    handleCarouselUpdate(api);
    initialized = true;
    // https://www.embla-carousel.com/api/events/#resize
    api.on('resize', () => {
        align = getAlignValue();
        handleCarouselUpdate(api);
    });
    // https://www.embla-carousel.com/api/events/#select
    api.on('select', () => handleCarouselUpdate(api));
    // https://www.embla-carousel.com/api/events/#pointerdown
    api.on('pointerDown', () => {
        isDragging = true;
    });
    // https://www.embla-carousel.com/api/events/#pointerup
    api.on('pointerUp', () => {
        isDragging = false;
    });
}
// https://www.embla-carousel.com/api/options/
const emblaOptions = $derived({
    align,
    watchDrag() {
        return isScrollable;
    },
    ...options,
});
// https://www.embla-carousel.com/plugins/
const emblaPlugins = [
    // https://www.embla-carousel.com/plugins/wheel-gestures/
    // Opting out of applying a class to the container during a wheel gesture by passing an empty string
    WheelGesturesPlugin({ wheelDraggingClass: '' }),
    ...plugins,
];
</script>

<div
  {...rest}
  class="CarouselStage {className}"
  class:CarouselStage--loop={options?.loop === true}
  data-dragging={isDragging}
  data-scrollable={isScrollable}
  style:--slide-flex-basis-medium="{100 / slidesToShow}%"
  style:--slide-flex-basis-small={slidesToShow === 1 ? '100%' : '50%'}
  style:--slide-flex-basis-base="{100 / slidesToShowOnMobile}%"
  style:--slide-columns={columnsOnMobile}
  style:--slide-offset-gap={columnsOnMobile === NUMBER_OF_GRID_COLUMNS ? 'var(--space-none)' : 'var(--slide-gap)'}
>
  <div
    class="CarouselStage__stage"
    use:emblaCarouselSvelte={{
      // AlignmentOptionType is not exported from Embla Carousel, so just ignoring the TS error
      options: emblaOptions,
      plugins: emblaPlugins,
    }}
    onemblaInit={onInit}
  >
    <div class="CarouselStage__slider">
      {#each slides as slide, idx}
        <div
          class="CarouselStage__slide"
          role="group"
          data-index={idx}
          aria-label="Slide {idx + 1} of {slides.length}"
          aria-hidden={idx < slidesToShow ? 'false' : 'true'}
          {...idx >= slidesToShow ? { inert: true } : {}}
          bind:this={slideElements[idx]}
        >
          {@render slideFromProps?.({ slide })}
        </div>
      {/each}
    </div>
  </div>
</div>

<style>/**
 * Do not edit directly, this file was auto-generated.
 * Wed, 16 Jul 2025 20:39:39 GMT
 */
/**
 We use rem units for media queries to promote accessible layouts. By utilizing rem units, the computed values in media queries are determined by the user's browser-defined font size rather than our global 'font-size: 62.5%' reset. This approach ensures that our design adapts to individual user settings, enhancing accessibility. 
 */
@layer design-system-components.pwds {
  .CarouselStage {
    --grid-columns: 12;
    --slide-gap: var(--space-20);
    cursor: grab;
    overflow: hidden;
  }
  .CarouselStage--loop .CarouselStage__slide:last-child {
    padding-right: var(--space-none);
  }
  .CarouselStage[data-dragging=true] {
    cursor: grabbing;
  }
  .CarouselStage[data-scrollable=false] {
    cursor: auto;
  }
  .CarouselStage__stage {
    width: var(--container-width-current);
    margin-right: var(--space-auto);
    margin-left: var(--space-auto);
  }
  .CarouselStage__slider {
    display: flex;
    margin-left: calc(var(--slide-gap) * -1);
    touch-action: pan-y pinch-zoom;
  }
  .CarouselStage__slide {
    flex: 0 0 calc(var(--slide-flex-basis-base) * var(--slide-columns) / var(--grid-columns));
    min-width: 0;
    padding-left: var(--slide-gap);
  }
  .CarouselStage__slide:last-child {
    flex: 0 0 calc(var(--slide-flex-basis-base) * var(--slide-columns) / var(--grid-columns) + var(--slide-offset-gap));
    padding-right: var(--slide-offset-gap);
  }
  @media (min-width: 46.25rem) {
    .CarouselStage__slide:last-child {
      padding-right: var(--space-none);
    }
  }
  @media (min-width: 46.25rem) {
    .CarouselStage__slide, .CarouselStage__slide:last-child {
      flex-basis: var(--slide-flex-basis-small);
    }
  }
  @media (min-width: 64rem) {
    .CarouselStage__slide, .CarouselStage__slide:last-child {
      flex-basis: var(--slide-flex-basis-medium);
    }
  }
}</style>
